import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TablePagination,
  TextField,
  Typography
} from '@material-ui/core'
import { Archive, Delete, Edit, Publish } from '@material-ui/icons'
import { DataTable, DataTableColumn, DataTableOptions, DataType, FilterType, useNotify } from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  archivePost,
  deletePost,
  getPost,
  getPosts,
  publishPost
} from '../../../api/Argentia/endpoints/atlasPosts/atlasPosts.api'
import {
  AtlasLanguage,
  AtlasPost,
  AtlasPostPage,
  AtlasPostSortBy,
  AtlasPostStatus
} from '../../../api/Argentia/endpoints/atlasPosts/model/atlasPosts'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { StandardCard } from '../../../components/shared/standardCard/standardCard'
import { NonAuthRoutes } from '../../../model/enums/nonAuthRoutes'

type Action = 'publish' | 'archive' | 'delete'
interface PendingAction {
  kind: Action
  row: AtlasPost
}

const dateLabel = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

const localDateTime = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

export const AtlasBlogPage: React.FC = () => {
  const [t] = useTranslation(['atlasBlog', 'globalerisLibrary'])
  const history = useHistory()
  const { error, info } = useNotify()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [status, setStatus] = useState<AtlasPostStatus | ''>('')
  const [language, setLanguage] = useState<AtlasLanguage | ''>('')
  const [sortBy, setSortBy] = useState<AtlasPostSortBy>('PublicationDate')
  const [descending, setDescending] = useState(true)
  const [result, setResult] = useState<AtlasPostPage>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 20
  })
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [publicationDate, setPublicationDate] = useState(localDateTime)

  useEffect(() => {
    let active = true
    setLoading(true)
    getPosts({
      page: page + 1,
      pageSize,
      estado: status || undefined,
      idioma: language || undefined,
      sortBy,
      descending
    })
      .then(data => {
        if (active) setResult(data)
      })
      .catch(() => {
        if (active) error(t('errors.list'))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [page, pageSize, status, language, sortBy, descending, reload])

  const openAction = async (kind: Action, row: AtlasPost) => {
    if (kind === 'publish') {
      setBusy(true)
      try {
        const full = await getPost(row.id)
        const text = (full.contenido || '').replace(/<[^>]*>/g, '').trim()
        const hasImage = /<img\b/i.test(full.contenido || '')
        if (
          !full.titulo?.trim() ||
          !full.resumen?.trim() ||
          (!text && !hasImage) ||
          !full.autor?.trim()
        ) {
          error(t('errors.incomplete'))
          return
        }
        setPublicationDate(localDateTime())
      } catch (_) {
        error(t('errors.detail'))
        return
      } finally {
        setBusy(false)
      }
    }
    setPending({ kind, row })
  }

  const confirm = async () => {
    if (!pending || busy) return
    let iso: string | undefined
    if (pending.kind === 'publish') {
      const parsed = new Date(publicationDate)
      if (!publicationDate || isNaN(parsed.getTime())) {
        error(t('errors.date'))
        return
      }
      iso = parsed.toISOString()
    }
    setBusy(true)
    try {
      if (pending.kind === 'publish') await publishPost(pending.row.id, iso!)
      if (pending.kind === 'archive') await archivePost(pending.row.id)
      if (pending.kind === 'delete') await deletePost(pending.row.id)
      info(t('messages.success'))
      setPending(null)
      if (pending.kind === 'delete' && result.items.length === 1 && page > 0) setPage(page - 1)
      else setReload(value => value + 1)
    } catch (_) {
      error(t('errors.action'))
    } finally {
      setBusy(false)
    }
  }

  const tableColumns: DataTableColumn[] = [
    { name: 'titleLabel', label: t('columns.title'), type: DataType.TEXT, sortable: false },
    { name: 'idioma', label: t('columns.language'), type: DataType.TEXT, sortable: false },
    { name: 'statusLabel', label: t('columns.status'), type: DataType.TEXT, sortable: false },
    { name: 'authorLabel', label: t('columns.author'), type: DataType.TEXT, sortable: false },
    { name: 'publicationDateLabel', label: t('columns.publicationDate'), type: DataType.TEXT, sortable: false }
  ]
  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 0,
    search: false,
    filterType: FilterType.NONE,
    download: false,
    print: false,
    pagination: false,
    selectableRows: false,
    canInsert: true,
    customInsertEditModal: true,
    fabLabel: t('add'),
    onRowInsert: async () => {
      history.push(NonAuthRoutes.AtlasBlogAdd)
      return true
    },
    noData: { description: t('empty') },
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('actions.edit'),
        onClick: row => history.push(`/atlas/blog/${row.id}`)
      },
      {
        icon: <Publish fontSize="small" />,
        tooltip: t('actions.publish'),
        visibleIf: 'canPublish',
        onClick: row => openAction('publish', row)
      },
      {
        icon: <Archive fontSize="small" />,
        tooltip: t('actions.archive'),
        visibleIf: 'canArchive',
        onClick: row => openAction('archive', row)
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('actions.delete'),
        visibleIf: 'canDelete',
        onClick: row => openAction('delete', row)
      }
    ]
  }
  const tableData = result.items.map(row => ({
    ...row,
    titleLabel: row.titulo || '—',
    statusLabel: t(`statuses.${row.estado}`),
    authorLabel: row.autor || '—',
    publicationDateLabel: dateLabel(row.fechaPublicacion),
    canPublish: !busy && row.estado !== 'Publicado',
    canArchive: !busy && row.estado === 'Publicado',
    canDelete: !busy
  }))

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <Box marginX={2}>
          <PageHeader title={t('title')} />
        </Box>
        <StandardCard variant="outlined" title={t('listTitle')}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={16} mb={2}>
            <TextField
              id="atlas-blog-status"
              select
              variant="outlined"
              label={t('columns.status')}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              style={{ flex: '1 1 150px', minWidth: 0 }}
              value={status}
              onChange={event => {
                setStatus(event.target.value as AtlasPostStatus | '')
                setPage(0)
              }}
            >
              <MenuItem value="">{t('all')}</MenuItem>
              <MenuItem value="Borrador">{t('statuses.Borrador')}</MenuItem>
              <MenuItem value="Publicado">{t('statuses.Publicado')}</MenuItem>
              <MenuItem value="Archivado">{t('statuses.Archivado')}</MenuItem>
            </TextField>
            <TextField
              id="atlas-blog-language"
              select
              variant="outlined"
              label={t('columns.language')}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              style={{ flex: '1 1 130px', minWidth: 0 }}
              value={language}
              onChange={event => {
                setLanguage(event.target.value as AtlasLanguage | '')
                setPage(0)
              }}
            >
              <MenuItem value="">{t('all')}</MenuItem>
              <MenuItem value="ES">ES</MenuItem>
              <MenuItem value="EN">EN</MenuItem>
            </TextField>
            <TextField
              id="atlas-blog-sort-by"
              select
              variant="outlined"
              label={t('sort.label')}
              style={{ flex: '1 1 220px', minWidth: 0 }}
              value={sortBy}
              onChange={event => {
                setSortBy(event.target.value as AtlasPostSortBy)
                setPage(0)
              }}
            >
              <MenuItem value="PublicationDate">{t('sort.PublicationDate')}</MenuItem>
              <MenuItem value="CreationDate">{t('sort.CreationDate')}</MenuItem>
              <MenuItem value="Title">{t('sort.Title')}</MenuItem>
            </TextField>
            <TextField
              id="atlas-blog-sort-order"
              select
              variant="outlined"
              label={t('sort.order')}
              style={{ flex: '1 1 150px', minWidth: 0 }}
              value={descending ? 'descending' : 'ascending'}
              onChange={event => {
                setDescending(event.target.value === 'descending')
                setPage(0)
              }}
            >
              <MenuItem value="descending">{t('sort.descending')}</MenuItem>
              <MenuItem value="ascending">{t('sort.ascending')}</MenuItem>
            </TextField>
          </Box>
          <DataTable data={loading ? undefined : tableData} columns={tableColumns} options={tableOptions} />
          {!loading && (
            <TablePagination
              component="div"
              count={result.totalCount}
              page={page}
              rowsPerPage={pageSize}
              onChangePage={(_, value) => setPage(value)}
              onChangeRowsPerPage={event => {
                setPageSize(Number(event.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          )}
        </StandardCard>
      </PageCenterColumn>
      <Dialog open={!!pending} onClose={() => !busy && setPending(null)} fullWidth maxWidth="sm">
        <DialogTitle>{pending ? t(`actions.${pending.kind}`) : ''}</DialogTitle>
        <DialogContent>
          <Typography>
            {pending ? t(`confirm.${pending.kind}`, { title: pending.row.titulo }) : ''}
          </Typography>
          {pending?.kind === 'publish' && (
            <TextField
              fullWidth
              margin="normal"
              type="datetime-local"
              label={t('publicationDate')}
              InputLabelProps={{ shrink: true }}
              value={publicationDate}
              onChange={event => setPublicationDate(event.target.value)}
            />
          )}
          {pending?.kind === 'publish' &&
            publicationDate &&
            !isNaN(new Date(publicationDate).getTime()) && (
              <Typography variant="body2">
                {t('confirm.visibleFrom', { date: new Date(publicationDate).toLocaleString() })}
              </Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPending(null)}>
            {t('cancel')}
          </Button>
          <Button disabled={busy} color="primary" onClick={confirm}>
            {t('confirm.button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  )
}
